import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { normalizePhoneForStorage } from '@/utils/phoneNormalization';
import { WorldClassPhoneInput } from '@/components/ui/WorldClassPhoneInput';
import { useAllCountries } from '@/hooks/useAllCountries';
import { ipLocationService } from '@/services/IPLocationService';
import { getDialCode } from '@/lib/phoneFormatUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { 
  Loader2, 
  Phone, 
  CheckCircle, 
  ShieldCheck, 
  Truck,
  Bell,
  Package,
  Lock,
  MessageCircle,
  Gift
} from 'lucide-react';

const phoneSchema = z.object({
  phone: z
    .string()
    .min(8, 'Phone number must be at least 8 digits')
    .max(20, 'Phone number must be less than 20 characters')
    .regex(/^\+?[\d\s\-\(\)]+$/, 'Please enter a valid phone number'),
});

type PhoneFormValues = z.infer<typeof phoneSchema>;

interface PhoneCollectionModalProps {
  // Support both interface patterns for backward compatibility
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isOpen?: boolean;
  onClose?: () => void;
  // Callback patterns
  onPhoneAdded?: () => void;
  onComplete?: () => void;
  // Customization
  title?: string;
  description?: string;
  skipOption?: boolean | { text: string; subtext?: string };
  benefits?: string[];
  // Feature flags
  showCountrySelection?: boolean;
  showBenefits?: boolean;
  useGradientStyling?: boolean;
}

export const PhoneCollectionModal: React.FC<PhoneCollectionModalProps> = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  onPhoneAdded,
  onComplete,
  title = 'Add Your Phone Number',
  description = 'We need your phone number to complete this action and keep you updated on your orders.',
  skipOption = false,
  benefits,
  showCountrySelection = false,
  showBenefits = false,
  useGradientStyling = true,
}) => {
  // Normalize props for backward compatibility
  const isModalOpen = open ?? isOpen ?? false;
  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange?.(newOpen);
    if (!newOpen) {
      onClose?.();
    }
  };
  const handleComplete = () => {
    onPhoneAdded?.();
    onComplete?.();
  };
  const { user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [detectedCountryCode, setDetectedCountryCode] = useState<string>('US');
  const { data: countries = [] } = useAllCountries();

  const form = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: '',
    },
  });

  // Detect user's country based on IP using the proper service
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Use the IPLocationService which handles CORS and rate limiting properly
        const locationData = await ipLocationService.detectCountry();
        console.log('IP Detection Response:', locationData);
        
        if (locationData.countryCode) {
          console.log('Detected country code:', locationData.countryCode);
          console.log('Available countries sample:', countries.slice(0, 5));
          
          // Map country code to phone code - use 'code' field instead of 'iso2'
          const country = countries.find(c => {
            if (!c.code) return false;
            return c.code.toUpperCase() === locationData.countryCode.toUpperCase();
          });
          
          console.log('Found country:', country);
          if (country) {
            setDetectedCountryCode(country.code);

            // Use getDialCode function to get the phone code from country code
            const dialCode = getDialCode(country.code);
            console.log('Setting dial code:', dialCode);
            form.setValue('phone', dialCode);

            // Force update the form field
            setTimeout(() => {
              form.setValue('phone', dialCode);
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Default to Nepal if detection fails
        const nepal = countries.find(c => c.code && c.code === 'NP');
        if (nepal) {
          setDetectedCountryCode('NP');
          const dialCode = getDialCode('NP');
          form.setValue('phone', dialCode);
        }
      }
    };

    if (countries.length > 0) {
      detectCountry();
    }
  }, [countries, form]);

  const handleSubmit = async (values: PhoneFormValues) => {
    if (!user) return;

    // Check if there's a phone validation error
    if (phoneError) {
      toast({
        title: 'Please fix form errors',
        description: 'Please enter a valid phone number.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Update phone in auth.users
      // Normalize phone to proper storage format
      const normalizedPhone = normalizePhoneForStorage(values.phone);
      console.log('[PhoneCollectionModal] Saving phone:', {
        original: values.phone,
        normalized: normalizedPhone,
        hasPlus: normalizedPhone.startsWith('+'),
        length: normalizedPhone.length
      });
      const { error: authError } = await supabase.auth.updateUser({
        phone: normalizedPhone,
      });

      if (authError) {
        toast({
          title: 'Error',
          description: authError.message,
          variant: 'destructive',
        });
        return;
      }

      // Note: Country selection has been simplified - no longer updating profile country here

      toast({
        title: 'Phone Number Added',
        description: 'Your phone number has been saved successfully.',
      });

      handleOpenChange(false);
      handleComplete();
    } catch (error) {
      console.error('Error updating phone:', error);
      toast({
        title: 'Error',
        description: 'Failed to save phone number. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    handleOpenChange(false);
    // Note: Don't call handleComplete since user skipped
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={
          useGradientStyling
            ? 'bg-white border-gray-200 shadow-2xl max-w-md rounded-xl overflow-hidden'
            : 'sm:max-w-[425px]'
        }
      >
        {useGradientStyling ? (
          // Gradient styling for auth context
          <div className="bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-400 px-6 pt-6 pb-4 -mx-6 -mt-6 mb-6">
            <DialogHeader className="space-y-3">
              <div className="mx-auto w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-2">
                <Phone className="h-6 w-6 text-white" />
              </div>
              <DialogTitle className="text-white text-xl font-semibold text-center">
                {title}
              </DialogTitle>
              <DialogDescription className="text-white/90 text-center text-sm">
                {description}
              </DialogDescription>
            </DialogHeader>
          </div>
        ) : (
          // Standard styling for onboarding context
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              {title}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
        )}

        {(showBenefits || benefits) && (
          <div className="grid gap-4 py-4">
            {benefits ? (
              <div className="space-y-3">
                {benefits.map((benefit, index) => {
                  // Map benefits to icons
                  const getIcon = () => {
                    if (benefit.toLowerCase().includes('sms') || benefit.toLowerCase().includes('updates')) {
                      return <Bell className="h-4 w-4 text-teal-600 flex-shrink-0" />;
                    } else if (benefit.toLowerCase().includes('delivery') || benefit.toLowerCase().includes('coordination')) {
                      return <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />;
                    } else if (benefit.toLowerCase().includes('security') || benefit.toLowerCase().includes('2fa')) {
                      return <Lock className="h-4 w-4 text-purple-600 flex-shrink-0" />;
                    } else if (benefit.toLowerCase().includes('support') || benefit.toLowerCase().includes('whatsapp')) {
                      return <MessageCircle className="h-4 w-4 text-green-600 flex-shrink-0" />;
                    } else if (benefit.toLowerCase().includes('deals') || benefit.toLowerCase().includes('exclusive')) {
                      return <Gift className="h-4 w-4 text-orange-600 flex-shrink-0" />;
                    }
                    return <CheckCircle className="h-4 w-4 text-gray-600 flex-shrink-0" />;
                  };
                  
                  return (
                    <div key={index} className="flex items-start gap-3 text-sm text-gray-700">
                      {getIcon()}
                      <span className="leading-relaxed">{benefit}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <Truck className="h-4 w-4" />
                  <span>Delivery updates and coordination</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Order security and verification</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className={showBenefits ? '' : 'space-y-6'}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel
                      className={
                        useGradientStyling ? 'text-sm font-medium text-gray-700' : undefined
                      }
                    >
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <WorldClassPhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        countries={countries}
                        disabled={isLoading}
                        initialCountry={detectedCountryCode}
                        currentCountry={detectedCountryCode}
                        placeholder="Enter your phone number"
                        className="w-full"
                      />
                    </FormControl>
                    {!phoneError && <FormMessage />}
                  </FormItem>
                )}
              />


              <div className="flex gap-3">
                {skipOption && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSkip}
                    disabled={isLoading}
                    className={
                      useGradientStyling
                        ? 'px-6 border-gray-200 text-gray-600 hover:bg-gray-50 rounded-lg h-11'
                        : 'flex-1'
                    }
                  >
                    {typeof skipOption === 'object' ? skipOption.text : 'Skip for Now'}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={
                    useGradientStyling
                      ? 'flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium rounded-lg transition-colors h-11'
                      : skipOption
                        ? 'flex-1'
                        : 'w-full'
                  }
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : useGradientStyling ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Save Phone Number
                    </>
                  ) : showCountrySelection ? (
                    'Save Phone'
                  ) : (
                    'Save Phone Number'
                  )}
                </Button>
              </div>
              
              {typeof skipOption === 'object' && skipOption.subtext && (
                <p className="text-center text-xs text-gray-500 mt-4">
                  {skipOption.subtext}
                </p>
              )}
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
